import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncablePriceAcknowledgementInput } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { PriceAcknowledgement as RxPriceAcknowledgement } from "../collections/price-acknowledgement";
import type { RxDBCollections } from "../provider";
import {
  SyncPriceAcknowledgementPullDocument,
  SyncPriceAcknowledgementPushDocument,
  type WholeSyncablePriceAcknowledgementFragment,
} from "./__generated__/price-acknowledgement.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePriceAcknowledgement = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxPriceAcknowledgement>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncablePriceAcknowledgementFragment> | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncPriceAcknowledgementPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  const push = useMemo(
    () =>
      enabled
        ? {
            mutation: SyncPriceAcknowledgementPushDocument,
            mapRxToGql: mapRxPriceAcknowledgementToGqlInput,
          }
        : undefined,
    [enabled],
  );

  return useGqlReplication<
    RxPriceAcknowledgement,
    WholeSyncablePriceAcknowledgementFragment,
    SyncablePriceAcknowledgementInput
  >({
    db,
    collection,
    pull,
    push,
    mapGqlToRx: mapGqlPriceAcknowledgementToRx,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "price-acknowledgement",
    enabled,
  });
};

export const mapGqlPriceAcknowledgementToRx = (
  ack: WholeSyncablePriceAcknowledgementFragment,
): WithDeleted<RxPriceAcknowledgement> => ({
  id: ack.id,
  userId: ack.userId,
  acknowledgedAt: ack.acknowledgedAt,
  meta: {
    createdAt: ack.meta.createdAt,
    createdByUserId: ack.meta.createdByUserId,
    updatedAt: ack.meta.updatedAt,
    updatedByUserId: ack.meta.updatedByUserId,
  },
  _deleted: ack.meta.deleted,
});

export const mapRxPriceAcknowledgementToGqlInput = (
  ack: WithDeleted<RxPriceAcknowledgement>,
): SyncablePriceAcknowledgementInput => ({
  id: ack.id,
  userId: ack.userId,
  acknowledgedAt: ack.acknowledgedAt,
  meta: {
    createdAt: ack.meta.createdAt,
    createdByUserId: ack.meta.createdByUserId,
    updatedAt: ack.meta.updatedAt,
    updatedByUserId: ack.meta.updatedByUserId,
    deleted: ack._deleted,
  },
});

gql`
  fragment WholeSyncablePriceAcknowledgement on SyncablePriceAcknowledgement {
    id
    userId
    acknowledgedAt
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncPriceAcknowledgementPull($input: PullPriceAcknowledgementInput!) {
    pullPriceAcknowledgement(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePriceAcknowledgement
      }
    }
  }
`;

gql`
  mutation SyncPriceAcknowledgementPush($input: PushPriceAcknowledgementInput!) {
    pushPriceAcknowledgement(input: $input) {
      rows {
        ...WholeSyncablePriceAcknowledgement
      }
    }
  }
`;
